import { useState } from 'react';
import { Link } from 'react-router';
import { useGrowth, useLaunchEmail, useMe } from '../api/hooks';
import type { Counted, Growth } from '../api/types';
import { PageHeader } from '../app/Shell';
import { BarChart, Button, Card, ConfirmDialog, DataTable, ErrorNote, Field, Icon, QueryView, ReasonField, Stat, StatGrid, SuccessNote, fmtDateTime, fmtInt, fmtPct, reasonError, toast } from '../ui';

/** Where a visitor came from (`?ref=`): the ones the website itself sets, else the tag as posted. */
const REF_LABEL: Record<string, string> = { direct: '直接打開', share: '分享漫畫的最後一頁', other: '其他（超出上限）' };
const LOCALE_LABEL: Record<string, string> = { 'zh-HK': '繁體中文', 'zh-CN': '簡體中文', en: '英文' };
const refLabel = (key: string) => REF_LABEL[key] ?? key;

/** 預約與分享: the pre-registration list with its launch email and gift, and how shared chapters travel. */
export function GrowthPage() {
  const q = useGrowth();
  return (
    <>
      <PageHeader
        title="預約與分享"
        description="官網預約名單、上線通知電郵和預約禮物（官網一次購買半價），以及分享出去的漫畫被打開和讀完的次數。只有數字，不顯示電郵地址。"
        actions={
          <Button onClick={() => void q.refetch()} busy={q.isFetching}>
            <Icon name="refresh" size={16} /> 重新整理
          </Button>
        }
      />
      <QueryView query={q} loading="正在統計…">
        {(d) => (
          <>
            <Prereg d={d.prereg} />
            <Shares d={d.shares} />
          </>
        )}
      </QueryView>
    </>
  );
}

function Prereg({ d }: { d: Growth['prereg'] }) {
  const unsent = d.total - d.notified;
  return (
    <>
      <StatGrid>
        <Stat label="預約人數" value={fmtInt(d.total)} hint={d.byLocale.map((l) => `${LOCALE_LABEL[l.key] ?? l.key} ${fmtInt(l.count)}`).join(' · ')} />
        <Stat label="已寄上線通知" value={fmtInt(d.notified)} hint={unsent ? `未寄 ${fmtInt(unsent)}` : '全部已寄'} />
        <Stat label="曾用半價券結帳" value={fmtInt(d.giftsClaimed)} hint={`佔已通知 ${fmtPct(d.giftsClaimed, d.notified)}`} />
        <Stat label="用了半價券" value={fmtInt(d.giftsUsed)} hint={`佔已通知 ${fmtPct(d.giftsUsed, d.notified)}`} />
      </StatGrid>
      <div className="grid-2">
        <Card title="每日預約" sub="過去 30 日（UTC）">
          {d.daily.length ? <BarChart data={d.daily.map((x) => ({ label: x.day.slice(5), value: x.count }))} format={fmtInt} ariaLabel="每日預約人數" /> : <p className="faint">過去 30 日沒有新預約。</p>}
        </Card>
        <Card title="預約來源" sub="網址的 ?ref=" flush>
          <CountTable rows={d.byRef} total={d.total} empty="還沒有預約。" />
        </Card>
      </div>
      <LaunchEmail unsent={unsent} launching={d.launching} />
    </>
  );
}

/** The launch email: once for everyone on the list (later sign-ups get it on the next send). Owner only, can't be taken back. */
function LaunchEmail({ unsent, launching }: { unsent: number; launching: boolean }) {
  const me = useMe();
  const send = useLaunchEmail();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const owner = !!me.data?.roles.includes('owner');
  return (
    <Card title="上線通知電郵" sub="只限擁有者 · 每人只寄一次 · 發出後不能收回">
      <div className="stack">
        <p className="small">
          寄給所有未收過通知的預約者：遊戲已上線、官網連結，以及預約禮物（用同一個電郵登入官網商店，一次購買半價；月卡、會員首月、章節券都適用）。電郵寄出後禮物才可以使用。
        </p>
        {launching && <p className="small" style={{ color: 'var(--warn)' }}>正在寄出中……完成前不能再寄。</p>}
        {send.isSuccess && <SuccessNote>已排隊寄給 {fmtInt(send.data.recipients)} 位預約者。</SuccessNote>}
        <div className="row">
          <Button variant="danger" disabled={!owner || launching || unsent === 0} onClick={() => setOpen(true)}>
            <Icon name="mail" size={16} /> 寄出上線通知（{fmtInt(unsent)} 位）…
          </Button>
        </div>
      </div>
      <ConfirmDialog
        open={open}
        title={`寄上線通知給 ${fmtInt(unsent)} 位預約者？`}
        tone="danger"
        confirmLabel="寄出"
        busy={send.isPending}
        canConfirm={!reasonError(reason) && typed.trim() === String(unsent)}
        onCancel={() => setOpen(false)}
        onConfirm={() =>
          send.mutate(reason.trim(), {
            onSuccess: () => {
              setOpen(false);
              setReason('');
              setTyped('');
              toast('上線通知已排隊寄出');
            },
          })
        }
        error={send.isError ? <ErrorNote error={send.error} /> : null}
      >
        <p className="small">寄出後半價券即時生效，電郵不能收回。</p>
        <Field label={`輸入人數 ${unsent} 以確認`}>{(id) => <input id={id} type="text" inputMode="numeric" value={typed} onChange={(e) => setTyped(e.target.value)} />}</Field>
        <ReasonField value={reason} onChange={setReason} />
      </ConfirmDialog>
    </Card>
  );
}

function Shares({ d }: { d: Growth['shares'] }) {
  return (
    <>
      <StatGrid>
        <Stat label="分享中的漫畫" value={fmtInt(d.active)} />
        <Stat label="打開次數" value={fmtInt(d.views)} hint={d.active ? `平均每篇 ${(d.views / d.active).toFixed(1)}` : undefined} />
        <Stat label="讀到最後" value={fmtInt(d.finished)} hint={`佔打開 ${fmtPct(d.finished, d.views)}`} />
        <Stat label="點進遊戲" value={fmtInt(d.cta)} hint={`佔打開 ${fmtPct(d.cta, d.views)}`} />
        <Stat label="由分享頁預約" value={fmtInt(d.preregs)} hint={`佔打開 ${fmtPct(d.preregs, d.views)}`} />
      </StatGrid>
      <Card title="打開來源" sub="分享連結的 ?ref=，全部漫畫合計" flush>
        <CountTable rows={d.refs} total={d.views} unit="次數" empty="還沒有人打開分享的漫畫。" />
      </Card>
      <Card title="最多人看的分享" sub={`首 ${d.top.length} 篇`} flush>
        <DataTable
          rows={d.top}
          rowKey={(r) => r.shareId}
          initialSort={{ key: 'views', dir: 'desc' }}
          pageSize={25}
          empty="還沒有分享中的漫畫。"
          columns={[
            {
              key: 'title',
              header: '漫畫',
              render: (r) => (
                <span className="stack" style={{ gap: 0 }}>
                  {r.url ? (
                    <a href={r.url} target="_blank" rel="noreferrer">
                      {r.title ?? r.shareId}
                    </a>
                  ) : (
                    (r.title ?? r.shareId)
                  )}
                  <span className="faint small nowrap">
                    <Link to={`/users/${r.userId}`}>{r.sharedBy ?? '（未建立角色）'}</Link> · {fmtDateTime(r.createdAt)}
                  </span>
                </span>
              ),
              sort: (r) => r.title ?? '',
            },
            { key: 'views', header: '打開', align: 'right', render: (r) => fmtInt(r.views), sort: (r) => r.views },
            { key: 'finished', header: '讀完', align: 'right', render: (r) => `${fmtInt(r.finished)}（${fmtPct(r.finished, r.views)}）`, sort: (r) => r.finished },
            { key: 'cta', header: '點進遊戲', align: 'right', render: (r) => fmtInt(r.cta), sort: (r) => r.cta },
            { key: 'refs', header: '來源', render: (r) => <span className="small">{r.refs.length ? r.refs.map((x) => `${refLabel(x.key)} ${fmtInt(x.count)}`).join(' · ') : <span className="faint">—</span>}</span> },
          ]}
        />
      </Card>
    </>
  );
}

function CountTable({ rows, total, unit = '人數', empty }: { rows: Counted[]; total: number; unit?: string; empty: string }) {
  return (
    <DataTable
      rows={rows}
      rowKey={(r) => r.key}
      initialSort={{ key: 'count', dir: 'desc' }}
      empty={empty}
      columns={[
        { key: 'key', header: '來源', render: (r) => <span className="nowrap">{refLabel(r.key)}</span>, sort: (r) => r.key },
        { key: 'count', header: unit, align: 'right', render: (r) => fmtInt(r.count), sort: (r) => r.count },
        { key: 'share', header: '佔比', align: 'right', render: (r) => fmtPct(r.count, total), sort: (r) => r.count },
      ]}
    />
  );
}
